import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./NegotiationBot.module.css";
import {
  getChatTime,
  getLatestRound,
  getMessageText,
  getMessageTimestamp,
  getSessionStatusText,
  isBuyerMessage,
} from "./negotiationUtils";

const getFirstPresentValue = (...values) =>
  values.find((value) => value !== null && value !== undefined && value !== "");

const getNumericPrice = (value) => {
  const numericValue = Number(String(value || "").replace(/,/g, ""));
  return Number.isFinite(numericValue) ? numericValue : null;
};

const getMessageDedupKey = (message = {}) => {
  const sender = isBuyerMessage(message) ? "buyer" : "vendor";
  const text = getMessageText(message).trim().replace(/\s+/g, " ").toLowerCase();
  const timestamp = getMessageTimestamp(message) || "";
  return `${sender}|${text}|${timestamp}`;
};

const RevisedOfferForm = ({
  sessionToken = "",
  vendorBidItemToken = "",
  rfqCode = "",
  itemName = "",
  currentQuotedPrice = "",
  loading,
  inboxLoading,
  onSubmit,
  latestSession,
  sessionDetail,
  vendorSessions = [],
  selectedSessionToken = "",
  onSelectSession,
  onSendMessage,
}) => {
  const [currentPrice, setCurrentPrice] = useState("");
  const [vendorMessage, setVendorMessage] = useState("");
  const [chatMessage, setChatMessage] = useState("");
  const [localMessages, setLocalMessages] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState("");
  const [finalOffer, setFinalOffer] = useState(false);
  const [activeVendorView, setActiveVendorView] = useState("offer");
  const streamRef = useRef(null);
  const hasNegotiationReference = Boolean(sessionToken || vendorBidItemToken);
  const rounds = Array.isArray(sessionDetail?.rounds) ? sessionDetail.rounds : [];
  const persistedMessages = useMemo(
    () => (Array.isArray(sessionDetail?.messages) ? sessionDetail.messages : []),
    [sessionDetail?.messages],
  );
  const visibleMessages = useMemo(
    () =>
      persistedMessages.reduce((messages, item) => {
        const text = getMessageText(item).trim();
        if (!text) return messages;

        const key = getMessageDedupKey(item);
        if (messages.some((message) => message.key === key)) return messages;

        messages.push({ key, item });
        return messages;
      }, []),
    [persistedMessages],
  );
  const latestRound = getLatestRound(sessionDetail);
  const activeInboxSession =
    vendorSessions.find(
      (session) => session.negotiation_session_token === selectedSessionToken,
    ) ||
    vendorSessions[0] ||
    {};
  const displayRfqCode =
    rfqCode || sessionDetail?.rfq_code || activeInboxSession?.rfq_code || "-";
  const displayItem =
    itemName ||
    sessionDetail?.item_name ||
    activeInboxSession?.item_name ||
    "Negotiation item";
  const displayCurrentPrice =
    getFirstPresentValue(
      activeInboxSession?.current_price,
      sessionDetail?.current_price,
      currentQuotedPrice,
      latestRound?.offered_price,
      sessionDetail?.initial_price,
    ) || "-";
  const initialOfferPrice =
    getFirstPresentValue(
      sessionDetail?.initial_price,
      activeInboxSession?.current_price,
      currentQuotedPrice,
    ) || "-";
  const statusText = getSessionStatusText(sessionDetail || latestSession);
  const firstBuyerMessageIndex = visibleMessages.findIndex(({ item }) =>
    isBuyerMessage(item),
  );

  useEffect(() => {
    streamRef.current?.scrollTo({
      top: streamRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [activeVendorView, rounds.length, persistedMessages.length, localMessages.length]);

  useEffect(() => {
    if (!localMessages.length || !persistedMessages.length) return;

    setLocalMessages((prev) =>
      prev.filter(
        (localMessage) =>
          !persistedMessages.some(
            (persistedMessage) =>
              !isBuyerMessage(persistedMessage) &&
              getMessageText(persistedMessage).trim() === localMessage.text.trim(),
          ),
      ),
    );
  }, [localMessages.length, persistedMessages]);

  const resetForm = () => {
    setCurrentPrice("");
    setVendorMessage("");
    setPaymentTerms("");
    setFinalOffer(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const revisedPrice = getNumericPrice(currentPrice);
    if (!hasNegotiationReference || !revisedPrice) return;

    const identityPayload = sessionToken
      ? { negotiation_session_token: sessionToken }
      : { vendor_bid_item_token: vendorBidItemToken };
    const submitSucceeded = await onSubmit({
      ...identityPayload,
      current_price: revisedPrice,
      vendor_message: vendorMessage.trim(),
      final_offer: finalOffer,
      conditions: paymentTerms.trim()
        ? {
            payment_terms: paymentTerms.trim(),
          }
        : {},
    });

    if (submitSucceeded === false) return;

    resetForm();
  };

  const sendVendorMessage = async () => {
    const trimmedMessage = chatMessage.trim();
    const activeSessionToken = selectedSessionToken || sessionToken;
    if (!trimmedMessage || !activeSessionToken) return;

    const pendingMessage = {
      id: `${Date.now()}`,
      text: trimmedMessage,
      createdAt: new Date().toISOString(),
    };
    setLocalMessages((prev) => [...prev, pendingMessage]);
    setChatMessage("");

    await onSendMessage?.({
      negotiationSessionToken: activeSessionToken,
      message: trimmedMessage,
    });
  };

  const renderPriceChangeEvents = () =>
    rounds.map((round, index) => {
      const previousOffer =
        index === 0 ? initialOfferPrice : rounds[index - 1]?.offered_price;

      return (
        <div
          className={styles.priceChangeEvent}
          key={round.negotiation_round_token || round.round}
        >
          <span className={styles.priceChangeLabel}>
            Revised offer submitted
          </span>
          <span className={styles.priceChangeValues}>
            <span className={styles.priceChangeOld}>{previousOffer || "-"}</span>
            <span className={styles.priceChangeArrow} aria-hidden="true">
              -&gt;
            </span>
            <span className={styles.priceChangeNew}>
              {round.offered_price || "-"}
            </span>
          </span>
          <small>{getChatTime(round.created_at)}</small>
        </div>
      );
    });

  return (
    <section className={styles.chatPanel}>
      <div className={styles.requestInbox}>
        <div className={styles.requestInboxTitle}>
          <span className={styles.eyebrow}>Negotiation Requests</span>
          <strong>{vendorSessions.length}</strong>
        </div>
        {inboxLoading ? (
          <div className={styles.emptyState}>Loading negotiation requests...</div>
        ) : vendorSessions.length > 0 ? (
          <div className={styles.sessionRail}>
            {vendorSessions.map((session) => (
              <button
                type="button"
                key={session.negotiation_session_token}
                className={
                  selectedSessionToken === session.negotiation_session_token
                    ? styles.sessionActive
                    : ""
                }
                onClick={() => onSelectSession?.(session.negotiation_session_token)}
              >
                <span title={session.item_name || session.rfq_code}>
                  {session.item_name || session.rfq_code || "-"}
                </span>
                <small>
                  {session.rfq_code || "-"} | Round {session.current_round ?? 0}/
                  {session.max_rounds ?? "-"}
                </small>
              </button>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>No negotiation requests found.</div>
        )}
      </div>

      <div className={styles.chatStatusHeader}>
        <div>
          <h4>{displayItem}</h4>
          <p>{displayRfqCode}</p>
        </div>
        <span className={styles.liveStatus}>
          <i></i>
          {statusText}
        </span>
      </div>

      <div className={styles.vendorMiniBar}>
        <span>Current/latest offer</span>
        <strong>{displayCurrentPrice}</strong>
      </div>

      <div className={`${styles.tabRow} ${styles.vendorTabRow}`}>
        <button
          type="button"
          className={activeVendorView === "offer" ? styles.tabActive : ""}
          onClick={() => setActiveVendorView("offer")}
        >
          Submit Revised Offer
        </button>
        <button
          type="button"
          className={activeVendorView === "chat" ? styles.tabActive : ""}
          onClick={() => setActiveVendorView("chat")}
        >
          Chat
        </button>
      </div>

      {activeVendorView === "chat" ? (
        <>
          <div
            className={`${styles.chatConversation} ${styles.vendorChatConversation}`}
            ref={streamRef}
          >
            {firstBuyerMessageIndex === -1 ? renderPriceChangeEvents() : null}

            {visibleMessages.map(({ item, key }, index) => {
              const text = getMessageText(item);
              const buyerMessage = isBuyerMessage(item);

              return (
                <React.Fragment
                  key={item.message_token || item.token || item.id || key || index}
                >
                  <div
                    className={
                      buyerMessage ? styles.messageRowLeft : styles.messageRowRight
                    }
                  >
                    {buyerMessage ? <div className={styles.avatarDot}>B</div> : null}
                    <div className={styles.messageStack}>
                      <span className={styles.senderLabel}>
                        {buyerMessage ? "Buyer" : "You"}
                      </span>
                      <div
                        className={
                          buyerMessage
                            ? styles.messageBubbleLeft
                            : styles.messageBubbleRight
                        }
                      >
                        {text}
                      </div>
                      <small>{getChatTime(getMessageTimestamp(item))}</small>
                    </div>
                  </div>
                  {index === firstBuyerMessageIndex
                    ? renderPriceChangeEvents()
                    : null}
                </React.Fragment>
              );
            })}

            {localMessages.map((item) => (
              <div className={styles.messageRowRight} key={item.id}>
                <div className={styles.messageStack}>
                  <span className={styles.senderLabel}>You</span>
                  <div className={styles.messageBubbleRight}>{item.text}</div>
                  <small>{getChatTime(item.createdAt)}</small>
                </div>
              </div>
            ))}

            {loading ? (
              <div className={styles.typingIndicator}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            ) : null}

            {!loading && rounds.length === 0 && visibleMessages.length === 0 ? (
              <div className={styles.emptyState}>No chat history yet.</div>
            ) : null}
          </div>

          <div className={styles.chatComposer}>
            <input
              value={chatMessage}
              placeholder="Type message..."
              onChange={(event) => setChatMessage(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") sendVendorMessage();
              }}
            />
            <button
              type="button"
              disabled={loading || !(selectedSessionToken || sessionToken)}
              onClick={sendVendorMessage}
            >
              Send
            </button>
          </div>
        </>
      ) : null}

      {!hasNegotiationReference ? (
        <div className={styles.errorBox}>
          Negotiation reference is missing. Please open this reply from the
          notification or RFQ negotiation link.
        </div>
      ) : null}

      {activeVendorView === "offer" ? (
        <form
          className={`${styles.offerModal} ${styles.vendorInlineOfferForm}`}
          onSubmit={handleSubmit}
        >
          <div className={styles.modalHeader}>
            <h4>Submit Revised Offer</h4>
          </div>
          <label>
            Current Price
            <input value={displayCurrentPrice} disabled />
          </label>
          <label>
            Revised Price
            <input
              type="number"
              min="1"
              value={currentPrice}
              onChange={(event) => setCurrentPrice(event.target.value)}
              placeholder="Enter revised price"
            />
          </label>
          <label>
            Message(Optional)
            <textarea
              value={vendorMessage}
              onChange={(event) => setVendorMessage(event.target.value)}
              placeholder="Add any context for this revision..."
            />
          </label>
          <label>
            Payment Terms
            <input
              value={paymentTerms}
              onChange={(event) => setPaymentTerms(event.target.value)}
              placeholder="30 days"
            />
          </label>
          <label className={styles.checkLabel}>
            <input
              type="checkbox"
              checked={finalOffer}
              onChange={(event) => setFinalOffer(event.target.checked)}
            />
            Final offer
          </label>
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={resetForm}
              disabled={loading}
            >
              Reset
            </button>
            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={loading || !hasNegotiationReference}
            >
              {loading ? "Submitting..." : "Submit Revised Offer"}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
};

export default RevisedOfferForm;
